/**
 * Normalised schedule model.
 *
 * Both the XER and P6-XML parsers emit this shape, so every downstream
 * consumer (DCMA, EVM, charts, tables) works against one vocabulary rather
 * than raw P6 column names. Durations and float are exposed in *days*,
 * converted using the activity's own calendar where the file provides one.
 */

export type ActivityStatus = 'Not Started' | 'In Progress' | 'Completed';

export type ActivityType =
  | 'Task'
  | 'Milestone'
  | 'Finish Milestone'
  | 'Level of Effort'
  | 'WBS Summary'
  | 'Resource Dependent'
  | 'Other';

export type RelationshipType = 'FS' | 'SS' | 'FF' | 'SF';

/**
 * P6 constraint codes. DCMA treats the four that pin an activity to a date
 * regardless of logic as "hard"; the rest only bound movement in one
 * direction and are not counted.
 */
export type ConstraintType =
  | 'Start On'
  | 'Start On or Before'
  | 'Start On or After'
  | 'Finish On'
  | 'Finish On or Before'
  | 'Finish On or After'
  | 'Mandatory Start'
  | 'Mandatory Finish'
  | 'As Late As Possible'
  | null;

export const HARD_CONSTRAINTS: ConstraintType[] = [
  'Mandatory Start',
  'Mandatory Finish',
  'Start On',
  'Finish On',
];

export interface Relationship {
  predecessorId: string;
  successorId: string;
  type: RelationshipType;
  lagHours: number;
  lagDays: number;
}

export interface ResourceAssignment {
  resourceId: string;
  budgetUnits: number;
  actualUnits: number;
  remainingUnits: number;
  budgetCost: number;
  actualCost: number;
  remainingCost: number;
}

export interface Activity {
  id: string;
  code: string;
  name: string;
  wbsId: string | null;
  calendarId: string | null;
  type: ActivityType;
  isMilestone: boolean;

  status: ActivityStatus;
  isComplete: boolean;
  isInProgress: boolean;
  isNotStarted: boolean;

  /** Planned / early dates as scheduled. */
  targetStart: Date | null;
  targetFinish: Date | null;
  actualStart: Date | null;
  actualFinish: Date | null;
  earlyStart: Date | null;
  earlyFinish: Date | null;
  lateStart: Date | null;
  lateFinish: Date | null;

  /** Best-available dates: actual where it exists, otherwise target. */
  start: Date | null;
  finish: Date | null;

  originalDurationDays: number;
  remainingDurationDays: number;
  totalFloatDays: number;
  freeFloatDays: number;
  percentComplete: number;

  /** Float-based criticality — the definition DCMA assumes. */
  isCritical: boolean;
  /** P6's longest-path flag. Only populated when the file was scheduled with
   *  multiple-float-path calculation on, so it is reported separately. */
  isDrivingPath: boolean;
  floatPath: number | null;

  constraintType: ConstraintType;
  constraintDate: Date | null;
  secondaryConstraintType: ConstraintType;
  hasHardConstraint: boolean;

  predecessors: Relationship[];
  successors: Relationship[];
  resources: ResourceAssignment[];

  budgetCost: number;
  actualCost: number;
  remainingCost: number;
  atCompletionCost: number;
  budgetUnits: number;
  actualUnits: number;

  /** Activity code assignments, keyed by code-type name (e.g. Discipline). */
  activityCodes: Record<string, string>;

  hoursPerDay: number;
  raw: Record<string, string>;
}

export interface WbsNode {
  id: string;
  parentId: string | null;
  name: string;
  code: string;
  sequence: number;
  isProjectNode: boolean;
}

export interface ResourceInfo {
  id: string;
  name: string;
  shortName: string;
  type: string;
}

export interface CalendarInfo {
  id: string;
  name: string;
  hoursPerDay: number;
}

export interface ScheduleModel {
  source: 'xer' | 'xml';
  fileName: string;
  projectId: string | null;
  projectName: string;

  /** PROJECT.last_recalc_date — the schedule's own "as of" date. Every
   *  time-relative metric keys off this, never off today's clock. */
  dataDate: Date | null;
  /** True when the data date had to be inferred rather than read. */
  dataDateInferred: boolean;
  plannedStart: Date | null;
  scheduledFinish: Date | null;

  /** Earliest start / latest finish across all activities. */
  start: Date | null;
  finish: Date | null;

  activities: Activity[];
  activityById: Map<string, Activity>;
  relationships: Relationship[];
  wbs: WbsNode[];
  wbsById: Map<string, WbsNode>;
  resources: Map<string, ResourceInfo>;
  calendars: Map<string, CalendarInfo>;
  activityCodeTypes: string[];

  defaultHoursPerDay: number;
  /** Parse-time caveats worth surfacing in the UI rather than swallowing. */
  warnings: string[];
}

export interface ParseProgress {
  (percent: number, stage?: string): void;
}
